import { Button, Dropdown, Field, makeStyles, Option, Text, tokens } from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { useState } from 'react';

import { HubApiError } from '@/api/client';
import { ErrorState, LoadingState, PageHeader, SectionCard } from '@/design-system';
import { useEnvStore } from '@/env/envStore';
import { HUB_ENV_FULL_LABEL } from '@/env/types';
import { spacing } from '@/theme/tokens';

import { useAiUsage } from './api';
import { CaveatsPanel } from './components/CaveatsPanel';
import { CostTrendChart } from './components/CostTrendChart';
import { EndpointsTable } from './components/EndpointsTable';
import { ModelsCard } from './components/ModelsCard';
import { MonthlyTable } from './components/MonthlyTable';
import { SummaryCards } from './components/SummaryCards';
import { DEFAULT_MONTHS, MONTHS_OPTIONS, type MonthsOption } from './types';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'space-between',
    gap: spacing.l,
    marginBottom: spacing.l,
  },
  refreshed: {
    color: tokens.colorNeutralForeground3,
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
  },
});

const isMonthsOption = (v: number): v is MonthsOption =>
  (MONTHS_OPTIONS as readonly number[]).includes(v);

interface ProblemDetails {
  title?: string;
  detail?: string;
}

const isProblemDetails = (v: unknown): v is ProblemDetails =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Title for the error state — uses the backend's RFC-7807 `title` when present. */
function errorTitle(err: unknown): string {
  if (err instanceof HubApiError && isProblemDetails(err.body) && typeof err.body.title === 'string') {
    return err.body.title;
  }
  return 'Failed to load AI usage';
}

/** Description for the error state — surfaces the backend's `detail` when present. */
function errorDescription(err: unknown): string {
  if (err instanceof HubApiError && isProblemDetails(err.body) && typeof err.body.detail === 'string') {
    return err.body.detail;
  }
  if (err instanceof HubApiError && typeof err.body === 'string' && err.body.length > 0) {
    return err.body;
  }
  return 'Try again, or check that the EHR App Insights workspace ID is configured for this environment.';
}

export function AiUsagePage() {
  const classes = useStyles();
  const env = useEnvStore((s) => s.env);
  const [months, setMonths] = useState<MonthsOption>(DEFAULT_MONTHS);
  const { data, isLoading, isError, error, refetch, isFetching } = useAiUsage(months);

  return (
    <div>
      <PageHeader
        title="AI usage"
        description={`AI / Document Intelligence usage and applied-pricing cost rollup for ${HUB_ENV_FULL_LABEL[env]}.`}
      />

      <div className={classes.toolbar}>
        <Field label="Window">
          <Dropdown
            value={`${String(months)} months`}
            selectedOptions={[String(months)]}
            onOptionSelect={(_, d) => {
              const v = Number(d.optionValue);
              if (!Number.isNaN(v) && isMonthsOption(v)) {
                setMonths(v);
              }
            }}
          >
            {MONTHS_OPTIONS.map((opt) => (
              <Option key={opt} value={String(opt)}>
                {`${String(opt)} months`}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <div>
          <Text className={classes.refreshed} size={200}>
            {data
              ? `Generated ${new Date(data.generatedAtMs).toLocaleTimeString()} (server-side; backend caches for ~10 min)`
              : ''}
          </Text>
          <Button
            icon={<ArrowClockwiseRegular />}
            appearance="subtle"
            size="small"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingState label="Loading AI usage…" />
      ) : isError || !data ? (
        <ErrorState
          title={errorTitle(error)}
          description={errorDescription(error)}
          retry={() => void refetch()}
        />
      ) : (
        <div className={classes.grid}>
          <SummaryCards report={data} />

          <SectionCard title="Cost trend" description="Stacked monthly cost — Token $ vs Document Intelligence $.">
            <CostTrendChart months={data.monthsData} />
          </SectionCard>

          <SectionCard title="Monthly breakdown">
            <MonthlyTable months={data.monthsData} />
          </SectionCard>

          <SectionCard title="Top endpoints" description="Per-route attribution across the report window, sorted by total cost.">
            <EndpointsTable endpoints={data.endpoints} />
          </SectionCard>

          <SectionCard title="Models &amp; pricing">
            <ModelsCard models={data.models} pricingSource={data.pricingSource} />
          </SectionCard>

          <CaveatsPanel caveats={data.caveats} />
        </div>
      )}
    </div>
  );
}
